import { Injectable, computed } from '@angular/core';
import { CurrentUserService } from '../auth/current-user.service';

/**
 * Role-group constants. Mirror roles share the capability of the role they are
 * "same as" on the backend seeder:
 * - ADMIN_LEVEL: broad CRUD (Director mirrors Admin).
 * - MANAGER_LEVEL: Manager-equivalent (Head of Branch, Assistance Manager and
 *   the two Sales Manager roles mirror Manager).
 * - AGENT_LEVEL: Agent-equivalent (Supervisor, Senior/Junior REN mirror Agent).
 */
export const ADMIN_LEVEL_ROLES: ReadonlyArray<string> = ['Superadmin', 'Director', 'Admin'];
export const MANAGER_LEVEL_ROLES: ReadonlyArray<string> = [
  'Head of Branch',
  'Manager',
  'Team Sales Manager',
  'Group Sales Manager',
  'Assistance Manager',
];
export const AGENT_LEVEL_ROLES: ReadonlyArray<string> = [
  'Agent',
  'Supervisor',
  'Senior REN',
  'Junior REN',
];

/**
 * Central role-aware helper for UI authorization decisions.
 *
 * Mirrors the backend RoleScope logic for UX purposes only —
 * the server remains authoritative.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {

  constructor(private currentUser: CurrentUserService) {}

  // ─── Role signals ──────────────────────────────────────────────────────

  isSuperadmin = computed(() => this.currentUser.userRole() === 'Superadmin');
  // Keep the original single-role signals intact for existing callers.
  isAdmin = computed(() => this.currentUser.userRole() === 'Admin');
  isManager = computed(() => this.currentUser.userRole() === 'Manager');
  isAgent = computed(() => this.currentUser.userRole() === 'Agent');

  // ─── Role-group (level) signals ─────────────────────────────────────────
  // These recognize the new mirror roles by their group so new roles never
  // fall through to deny.
  isAdminLevel = computed(() => ADMIN_LEVEL_ROLES.includes(this.currentUser.userRole() ?? ''));
  isManagerLevel = computed(() => MANAGER_LEVEL_ROLES.includes(this.currentUser.userRole() ?? ''));
  isAgentLevel = computed(() => AGENT_LEVEL_ROLES.includes(this.currentUser.userRole() ?? ''));

  // ─── Authorization helpers ─────────────────────────────────────────────

  /**
   * Can the current user manage (edit/delete) the given resource?
   *
   * The resource should carry at least `organization_id` and optionally
   * `agent_id`, `agent_group_id`, and `is_shared`.
   *
   * Rules (matching backend):
   * - Superadmin → true (org required on submit, enforced elsewhere).
   * - Admin → same org.
   * - Manager → same org + same agent_group + not shared.
   * - Agent → same org + own agent_id + not shared.
   */
  canManage(resource: {
    organization_id?: number | null;
    agent_id?: number | null;
    agent_group_id?: number | null;
    is_shared?: boolean;
  }): boolean {
    if (this.isSuperadmin()) {
      return true;
    }

    const userOrgId = this.currentUser.organizationId();

    // Not same organization → cannot manage.
    if (resource.organization_id != null && userOrgId != null && resource.organization_id !== userOrgId) {
      return false;
    }

    // Admin-level (Admin + Director mirror) → manage same org.
    if (this.isAdminLevel()) {
      return true;
    }

    // Shared resources are view-only for Manager-level and Agent-level.
    if (resource.is_shared) {
      return false;
    }

    // Manager-level (Manager + Head of Branch / Assistance Manager / Sales Managers).
    if (this.isManagerLevel()) {
      const userGroupId = this.currentUser.agentGroupId();
      if (resource.agent_group_id != null && userGroupId != null) {
        return resource.agent_group_id === userGroupId;
      }
      // If no agent_group_id on resource, allow (server will ultimately enforce).
      return true;
    }

    // Agent-level (Agent + Supervisor / Senior REN / Junior REN).
    if (this.isAgentLevel()) {
      const userAgentId = this.currentUser.agentId();
      if (resource.agent_id != null && userAgentId != null) {
        return resource.agent_id === userAgentId;
      }
      // If no agent_id on resource, deny by default for Agent-level.
      return false;
    }

    return false;
  }

  /**
   * Whether the current user needs to select an organization before
   * performing manage (write) actions on org-scoped pages.
   */
  requiresOrganizationSelection(): boolean {
    return this.isSuperadmin();
  }

  // ─── Ability (permission) helpers ───────────────────────────────────────

  /**
   * Whether the current user holds the given spatie ability name
   * (e.g. "read_one.Project"). Delegates to the stored permissions.
   */
  hasPermission(ability: string): boolean {
    return this.currentUser.hasPermission(ability);
  }

  /**
   * Whether the current user holds ANY of the given ability names.
   */
  hasAnyPermission(abilities: string[]): boolean {
    return abilities.some((ability) => this.currentUser.hasPermission(ability));
  }
}
